import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';

import Button from 'uikit/Button';
import CleanedText from 'uikit/CleanedText';
import HelpTooltip from 'uikit/HelpTooltip';
import Icon from 'uikit/Icon';

import styles from './Questions.module.scss';

const cx = classNames.bind(styles);

const Questions = ({
  className,
  count,
  handleSectionToggle,
  isSectionOpen,
  t,
  table,
}) => (
  <div className={cx('container', className, { isSectionOpen })}>
    <button
      className={cx('header')}
      onClick={handleSectionToggle}
      type="button"
    >
      <div className={cx('title-container')}>
        <Icon className={cx('title-icon')} name="question-circle" />
        <h2 className={cx('title')}>
          {t('Course/Teacher/Statistics/Questions/title')}
          <span className={cx('count')}>{count}</span>
        </h2>
      </div>
      <Icon
        className={cx('toggle-button')}
        name={isSectionOpen ? 'chevron-up' : 'chevron-down'}
      />
    </button>
    {isSectionOpen && (
      <div className={cx('content')}>
        <table className={cx('table')} {...table.getTableProps()}>
          <thead>
            {table.headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    <div
                      className={cx('header')}
                      {...column.getSortByToggleProps()}
                    >
                      {column.render('Header')}
                      {column.id === 'rate' && (
                        <HelpTooltip
                          className={cx('tooltip')}
                          tooltip={t(
                            'Course/Teacher/Statistics/Questions/success-tooltip',
                          )}
                        />
                      )}
                      {column.canSort && (
                        <Icon
                          className={cx('sort')}
                          name={
                            column.isSorted && column.isSortedDesc
                              ? 'sort-down'
                              : column.isSorted
                              ? 'sort-up'
                              : 'sort'
                          }
                          variant="duotone"
                        />
                      )}
                    </div>
                    <div className={cx('filter')}>
                      {column.canFilter && column.render('Filter')}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...table.getTableBodyProps()}>
            {table.page.map((row) => {
              table.prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    if (
                      typeof cell.value === 'string' &&
                      cell.value.includes('$')
                    ) {
                      return (
                        <td className={cx('data')} {...cell.getCellProps()}>
                          <CleanedText>{cell.value}</CleanedText>
                        </td>
                      );
                    }
                    return (
                      <td className={cx('data')} {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className={cx('pagination')}>
          <div>
            <Button
              className={cx('navigation')}
              icon="chevron-double-left"
              isDisabled={!table.canPreviousPage}
              onClick={() => table.gotoPage(0)}
              tooltip={t('Course/Teacher/Statistics/Questions/first-page')}
            />
            <Button
              className={cx('navigation')}
              icon="chevron-left"
              isDisabled={!table.canPreviousPage}
              onClick={() => table.previousPage()}
              tooltip={t('Course/Teacher/Statistics/Questions/previous-page')}
            />
            <Button
              className={cx('navigation')}
              icon="chevron-right"
              isDisabled={!table.canNextPage}
              onClick={() => table.nextPage()}
              tooltip={t('Course/Teacher/Statistics/Questions/next-page')}
            />
            <Button
              className={cx('navigation')}
              icon="chevron-double-right"
              isDisabled={!table.canNextPage}
              onClick={() => table.gotoPage(table.pageCount - 1)}
              tooltip={t('Course/Teacher/Statistics/Questions/last-page')}
            />
          </div>
          <div>
            <span className={cx('current-page')}>
              {t('Course/Teacher/Statistics/Questions/page-count', {
                index: table.state.pageIndex + 1,
                of: table.pageOptions.length,
              })}
            </span>
            <select
              aria-label={t(
                'Course/Teacher/Statistics/Questions/aria-questions-per-page',
              )}
              className={cx('select')}
              value={table.state.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {t('Course/Teacher/Statistics/Questions/page-size', {
                    size: pageSize,
                  })}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    )}
  </div>
);

Questions.propTypes = {
  className: PropTypes.string,
  count: PropTypes.number.isRequired,
  handleSectionToggle: PropTypes.func.isRequired,
  isSectionOpen: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  table: PropTypes.object.isRequired,
};

Questions.defaultProps = {
  className: undefined,
};

export default Questions;
